import { useState, useEffect, useRef } from "react";
import Masonry from "react-masonry-css";
import { motion, AnimatePresence } from "framer-motion";
import "../css/masonryGallery.css";
import { Plus, ChevronLeft, ChevronRight } from "lucide-react";

function MasonryGallery({ gallery, variant = "default" }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentSlide, setCurrentSlide] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const breakpointColumnsObj = {
    default: 4,
    1400: 3,
    900: 2,
    768: 2,
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % gallery.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? gallery.length - 1 : prev - 1));
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    const distance = touchStartX.current - touchEndX.current;
    if (distance > 50) nextSlide();
    if (distance < -50) prevSlide();
  };

  const closeModal = () => setSelectedIndex(null);

  if (!gallery || gallery.length === 0) return null;

  return (
    <div>
      {/* 🖥 DESKTOP MASONRY */}
      {!isMobile && (
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className="masonry-grid"
          columnClassName="masonry-column"
        >
          {gallery.map((url, index) => (
            <motion.div
              key={index}
              className="gallery-item"
              whileHover={{ scale: 1.03 }}
              transition={{ duration: 0.3 }}
              onClick={() => setSelectedIndex(index)}
            >
              <img src={url} alt={`Imagen ${index + 1}`} />
              <div className="hover-overlay">
                <Plus size={32} strokeWidth={2.5} />
              </div>
            </motion.div>
          ))}
        </Masonry>
      )}

      {/* 📱 MOBILE GRID PARA LOGOS */}
      {isMobile && variant === "logos" && (
        <div className="mobile-logos-grid">
          {gallery.map((url, index) => (
            <div
              key={index}
              className="logo-item"
              onClick={() => setSelectedIndex(index)}
            >
              <img src={url} alt={`Logo ${index + 1}`} />
            </div>
          ))}
        </div>
      )}

      {/* 📱 MOBILE SLIDER NORMAL */}
      {isMobile && variant !== "logos" && (
        <div className="mobile-slider">
          <div
            className="slider-track"
            style={{
              transform: `translateX(-${currentSlide * 100}%)`,
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {gallery.map((url, index) => (
              <div
                key={index}
                className="slide"
                onClick={() => setSelectedIndex(index)}
              >
                <img src={url} alt={`Imagen ${index + 1}`} />
              </div>
            ))}
          </div>

          <button className="mobile-nav left" onClick={prevSlide}>
            <ChevronLeft size={28} />
          </button>

          <button className="mobile-nav right" onClick={nextSlide}>
            <ChevronRight size={28} />
          </button>
        </div>
      )}

      {/* MODAL */}
      <AnimatePresence>
        {selectedIndex !== null && (
          <motion.div
            className="modal-overlay-gallery"
            onClick={closeModal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.img
              key={gallery[selectedIndex]}
              src={gallery[selectedIndex]}
              className="modal-image"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.4 }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default MasonryGallery;
